import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { 
  ArrowLeft, DollarSign, Clock, Calendar, Gavel, FileText, MapPin, 
  BadgeDollarSign, BarChart, User, TrendingUp, Building, FileSpreadsheet,
  Mail, Lock, X
} from 'lucide-react';

interface Lance {
  id: number;
  pregaoId: string;
  empresa: string;
  valor: number;
  data: string;
  status: 'aceito' | 'pendente' | 'rejeitado';
}

interface Pregao {
  id: string;
  titulo: string;
  dataInicio: string;
  dataFim: string;
  status: 'agendado' | 'em_andamento' | 'finalizado' | 'cancelado';
  valorEstimado: number;
  descricao: string;
  categoria: string;
  documentos?: {
    nome: string;
    tipo: string;
    tamanho: string;
    dataUpload: string;
  }[];
  regiao?: string;
  impactoSocial?: string;
  participantes?: number;
  valorMinimoLance?: number;
}

const PregaoDetalhe: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [pregao, setPregao] = useState<Pregao | null>(null);
  const [ultimosLances, setUltimosLances] = useState<Lance[]>([]);
  const [modalLoginAberto, setModalLoginAberto] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [senha, setSenha] = useState<string>('');
  const [lembrar, setLembrar] = useState<boolean>(false);
  
  // Mock de dados para o pregão específico
  useEffect(() => {
    // Em um cenário real, aqui você faria uma chamada à API para buscar os dados do pregão pelo ID
    // Simulando um pregão para demonstração
    const mockPregao: Pregao = {
      id: 'PG-2025-002',
      titulo: 'Créditos de Carbono - Energia Solar',
      dataInicio: '2025-05-10T10:00:00',
      dataFim: '2025-05-10T16:00:00',
      status: 'em_andamento',
      valorEstimado: 850000,
      descricao: 'Aquisição de 15.000 créditos de carbono certificados provenientes de projetos de energia solar no Nordeste brasileiro.',
      categoria: 'Energia Renovável',
      regiao: 'Nordeste',
      impactoSocial: 'Alto',
      participantes: 7,
      valorMinimoLance: 25000,
      documentos: [
        {
          nome: 'Edital.pdf',
          tipo: 'PDF',
          tamanho: '2.3 MB',
          dataUpload: '2025-04-01T10:00:00'
        },
        {
          nome: 'Anexo_Técnico.pdf',
          tipo: 'PDF',
          tamanho: '4.1 MB',
          dataUpload: '2025-04-01T10:05:00'
        },
        {
          nome: 'Termo_de_Referência.docx',
          tipo: 'DOCX',
          tamanho: '1.8 MB',
          dataUpload: '2025-04-01T10:10:00'
        }
      ]
    };
    
    // Ajusta os dados do pregão com base no ID
    if (id === 'PG-2025-003') {
      mockPregao.id = 'PG-2025-003';
      mockPregao.titulo = 'Créditos de Carbono - Restauração de Manguezais';
      mockPregao.dataInicio = '2025-04-05T08:00:00';
      mockPregao.dataFim = '2025-04-05T14:00:00';
      mockPregao.status = 'finalizado';
      mockPregao.valorEstimado = 650000;
      mockPregao.descricao = 'Aquisição de 12.000 créditos de carbono provenientes de projetos de restauração de manguezais na costa brasileira.';
      mockPregao.categoria = 'Costeiro';
      mockPregao.regiao = 'Sul';
    } else if (id === 'PG-2025-004') {
      mockPregao.id = 'PG-2025-004';
      mockPregao.titulo = 'Créditos de Carbono - Biogás Rural';
      mockPregao.dataInicio = '2025-04-20T09:30:00';
      mockPregao.dataFim = '2025-04-20T15:30:00';
      mockPregao.status = 'cancelado';
      mockPregao.valorEstimado = 430000;
      mockPregao.descricao = 'Aquisição de 8.000 créditos de carbono de projetos de produção de biogás a partir de resíduos agrícolas.';
      mockPregao.categoria = 'Agropecuário';
      mockPregao.regiao = 'Centro-Oeste';
    }
    
    setPregao(mockPregao);
    
    // Simular últimos lances
    if (mockPregao.status === 'em_andamento') {
      const mockLances: Lance[] = [
        { 
          id: 1, 
          pregaoId: mockPregao.id,
          empresa: 'EcoGreen Energia', 
          valor: 38500, 
          data: '2025-05-10T10:15:23',
          status: 'aceito'
        },
        { 
          id: 2, 
          pregaoId: mockPregao.id,
          empresa: 'BrasilCarbon', 
          valor: 32000, 
          data: '2025-05-10T10:30:12',
          status: 'aceito'
        },
        { 
          id: 3, 
          pregaoId: mockPregao.id,
          empresa: 'Solar Sustainability', 
          valor: 27500, 
          data: '2025-05-10T11:05:45',
          status: 'aceito'
        }
      ];
      setUltimosLances(mockLances);
    }
  }, [id]);
  
  const formatarData = (dataString: string): string => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  const formatarHora = (dataString: string): string => {
    const data = new Date(dataString);
    return data.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const formatarValor = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };
  
  const obterStatusInfo = (status: Pregao['status']) => {
    switch (status) {
      case 'agendado':
        return { 
          texto: 'Agendado', 
          icone: <Calendar className="w-4 h-4 mr-1" />, 
          cor: 'bg-blue-100 text-blue-800'
        };
      case 'em_andamento':
        return { 
          texto: 'Em Andamento', 
          icone: <Clock className="w-4 h-4 mr-1" />, 
          cor: 'bg-amber-100 text-amber-800'
        };
      case 'finalizado':
        return { 
          texto: 'Finalizado', 
          icone: <FileText className="w-4 h-4 mr-1" />, 
          cor: 'bg-green-100 text-green-800'
        };
      case 'cancelado':
        return { 
          texto: 'Cancelado', 
          icone: <FileText className="w-4 h-4 mr-1" />, 
          cor: 'bg-red-100 text-red-800'
        };
      default:
        return { 
          texto: 'Desconhecido', 
          icone: null, 
          cor: 'bg-gray-100 text-gray-800'
        };
    }
  };
  
  // Abrir modal de login
  const abrirModalLogin = () => {
    setModalLoginAberto(true);
  };
  
  // Fechar modal de login
  const fecharModalLogin = () => {
    setModalLoginAberto(false);
  };
  
  // Processar login e navegar para página de lances
  const processarLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // Em um ambiente real, aqui seria feita a autenticação
    console.log('Login processado', { email, senha, lembrar });
    fecharModalLogin();
    navigate(`/pregao-lances/${id}`);
  };
  
  // Função antiga modificada para abrir modal
  const participarDoPregao = () => {
    abrirModalLogin();
  };
  
  const voltarParaDashboard = () => {
    navigate('/pregao-dashboard');
  };
  
  if (!pregao) {
    return <div className="flex justify-center items-center h-screen">Carregando...</div>;
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      
      {/* Cabeçalho */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="container mx-auto">
          <div className="flex items-center mb-4">
            <button 
              className="mr-4 p-2 rounded-full hover:bg-gray-100"
              onClick={voltarParaDashboard}
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Detalhes do Pregão <span className="text-[#0e7cb4]">Código: {pregao.id}</span></h1>
              <p className="text-gray-600">Visualização detalhada do pregão eletrônico</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Conteúdo Principal */}
      <div className="flex-grow p-6 bg-gray-50">
        <div className="container mx-auto">
          {/* Cabeçalho do Pregão */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row justify-between">
                <div className="flex-grow">
                  <div className="flex items-center mb-3">
                    <Gavel className="w-5 h-5 text-[#0e7cb4] mr-2" />
                    <h3 className="text-lg font-semibold">{pregao.titulo}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{pregao.descricao}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <Calendar className="h-4 w-4 mr-1 text-gray-500" />
                        <span>Início: {formatarData(pregao.dataInicio)} às {formatarHora(pregao.dataInicio)}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 mr-1 text-gray-500" />
                        <span>Término: {formatarData(pregao.dataFim)} às {formatarHora(pregao.dataFim)}</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <DollarSign className="h-4 w-4 mr-1 text-gray-500" />
                        <span>Valor estimado: {formatarValor(pregao.valorEstimado)}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <BadgeDollarSign className="h-4 w-4 mr-1 text-gray-500" />
                        <span>Lance mínimo: {formatarValor(pregao.valorMinimoLance || 0)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <FileSpreadsheet className="h-4 w-4 mr-1 text-gray-500" />
                      <span>Código: {pregao.id}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <BarChart className="h-4 w-4 mr-1 text-gray-500" />
                      <span>Categoria: {pregao.categoria}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-1 text-gray-500" />
                      <span>Região: {pregao.regiao}</span>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs flex items-center ${obterStatusInfo(pregao.status).cor}`}>
                      {obterStatusInfo(pregao.status).icone}
                      <span>{obterStatusInfo(pregao.status).texto}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 md:ml-4 flex flex-col items-center md:items-end">
                  <div className="mb-3">
                    <div className="text-sm text-gray-500 mb-1 text-center md:text-right">Participantes</div>
                    <div className="font-semibold text-xl text-[#0e7cb4] flex items-center">
                      <User className="h-5 w-5 mr-1" />
                      {pregao.participantes || 0}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Detalhes e Documentos */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Detalhes do Pregão */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Detalhes do Pregão</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-md font-medium mb-2">Descrição Completa</h3>
                      <p className="text-gray-600">
                        {pregao.descricao}
                      </p>
                      <p className="text-gray-600 mt-2">
                        Este pregão eletrônico visa a aquisição de créditos de carbono certificados para compensação 
                        voluntária de emissões. Os créditos serão provenientes de projetos de {pregao.categoria.toLowerCase()} 
                        localizados na região {pregao.regiao}, com impacto social {pregao.impactoSocial?.toLowerCase() || 'significativo'}.
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-md font-medium mb-2">Regras de Participação</h3>
                      <ul className="list-disc list-inside text-gray-600 space-y-1">
                        <li>Apenas empresas cadastradas e verificadas podem participar</li>
                        <li>Lance mínimo: {formatarValor(pregao.valorMinimoLance || 0)}</li>
                        <li>Os lances devem ser superiores ao último lance registrado</li>
                        <li>Não é permitido cancelar um lance após envio</li>
                        <li>Em caso de empate, o lance com registro mais antigo prevalece</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="text-md font-medium mb-2">Cronograma</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Abertura das Inscrições:</span>
                          <span className="font-medium">{formatarData(new Date(new Date(pregao.dataInicio).getTime() - 7 * 24 * 60 * 60 * 1000).toISOString())}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Fechamento das Inscrições:</span>
                          <span className="font-medium">{formatarData(new Date(new Date(pregao.dataInicio).getTime() - 1 * 24 * 60 * 60 * 1000).toISOString())}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Início do Pregão:</span>
                          <span className="font-medium">{formatarData(pregao.dataInicio)} às {formatarHora(pregao.dataInicio)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Término do Pregão:</span>
                          <span className="font-medium">{formatarData(pregao.dataFim)} às {formatarHora(pregao.dataFim)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Divulgação do Resultado:</span>
                          <span className="font-medium">{formatarData(new Date(new Date(pregao.dataFim).getTime() + 1 * 24 * 60 * 60 * 1000).toISOString())}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Documentos e Últimos Lances */}
            <div className="lg:col-span-1 space-y-6">
              {/* Documentos */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-4">Documentos</h2>
                  
                  {pregao.documentos && pregao.documentos.length > 0 ? (
                    <div className="space-y-3">
                      {pregao.documentos.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition">
                          <div className="flex items-center">
                            <FileText className="h-4 w-4 text-[#0e7cb4] mr-2" />
                            <div>
                              <div className="font-medium text-sm">{doc.nome}</div>
                              <div className="text-xs text-gray-500">{doc.tamanho} • {doc.tipo}</div>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" className="text-[#0e7cb4]">
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      Nenhum documento disponível
                    </p>
                  )}
                </CardContent>
              </Card>
              
              {pregao.status === 'em_andamento' && (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex justify-between mb-4">
                      <h2 className="text-lg font-semibold">Participar deste Pregão</h2>
                    </div>
                    
                    <div className="bg-amber-50 p-4 rounded-lg mb-4 border border-amber-200">
                      <p className="text-sm text-amber-800">
                        Envie seu lance para participar deste pregão eletrônico de créditos de carbono.
                      </p>
                    </div>
                    
                    <div className="mt-2">
                      <Button 
                        className="w-full bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                        onClick={participarDoPregao}
                      >
                        <TrendingUp className="h-5 w-5 mr-2" />
                        Participar Agora
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal de Login */}
      {modalLoginAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-800">Acesso ao Pregão</h2>
              <button 
                className="p-2 rounded-full hover:bg-gray-100"
                onClick={fecharModalLogin}
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <form onSubmit={processarLogin} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0e7cb4] focus:border-[#0e7cb4]"
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="senha">
                    Senha
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="senha"
                      type="password"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0e7cb4] focus:border-[#0e7cb4]"
                      placeholder="********"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="lembrar"
                      type="checkbox"
                      checked={lembrar}
                      onChange={(e) => setLembrar(e.target.checked)}
                      className="h-4 w-4 text-[#0e7cb4] focus:ring-[#0e7cb4] border-gray-300 rounded"
                    />
                    <label htmlFor="lembrar" className="ml-2 block text-sm text-gray-700">
                      Lembrar-me
                    </label>
                  </div>
                  
                  <div className="text-sm">
                    <a href="#" className="font-medium text-[#0e7cb4] hover:text-[#0a5c87]">
                      Esqueceu a senha?
                    </a>
                  </div>
                </div>
                
                <div>
                  <Button
                    type="submit"
                    className="w-full bg-[#0e7cb4] hover:bg-[#0a5c87] text-white py-2"
                  >
                    Entrar e Participar
                  </Button>
                </div>
                
                <div className="mt-4 text-center text-sm">
                  <span className="text-gray-600">Não tem uma conta? </span>
                  <a href="#" className="font-medium text-[#0e7cb4] hover:text-[#0a5c87]">
                    Cadastre-se
                  </a>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default PregaoDetalhe; 